import React, { useState, useEffect } from 'react';
import { X, Image as ImageIcon } from 'lucide-react';
import { toastSuccess, toastError } from '../../../utils/toast';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const inputStyle = { width: '100%', minHeight: '48px', padding: '0 20px', background: '#FBF9D9', border: 'none', borderRadius: '999px', fontFamily: 'Poppins, sans-serif', fontSize: '14.5px', color: '#051004', outline: 'none' };
const textareaStyle = { width: '100%', padding: '14px 20px', background: '#FBF9D9', border: 'none', borderRadius: '20px', fontFamily: 'Poppins, sans-serif', fontSize: '14.5px', color: '#051004', outline: 'none', resize: 'vertical' };
const labelStyle = { display: 'block', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', marginBottom: '8px' };
const errorStyle = { margin: '6px 0 0', fontSize: '12.5px', color: '#A32C0F' };

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
    style={{ border: 'none', cursor: 'pointer', width: '50px', height: '28px', borderRadius: '999px', padding: '3px', display: 'flex', background: checked ? '#72B744' : 'rgba(5,16,4,0.22)', transition: 'background 180ms ease', flexShrink: 0 }}
  >
    <span style={{ width: '22px', height: '22px', borderRadius: '999px', background: '#FCFBF5', transform: checked ? 'translateX(22px)' : 'translateX(0)', transition: 'transform 180ms ease' }} />
  </button>
);

const MenuModal = ({ isOpen, onClose, menu, onSuccess, availableDishes }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    is_available: true
  });
  const [selectedDishIds, setSelectedDishIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  // URLs d'images par défaut pour les menus
  const defaultImages = [
    'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1551782450-17144efb5723?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=300&fit=crop'
  ];

  useEffect(() => {
    if (menu) {
      const imageUrl = menu.image_url || menu.dish_image_url || '';
      setFormData({
        name: menu.name || '',
        description: menu.description || '',
        image_url: imageUrl,
        price: menu.price != null && menu.price !== '' ? String(menu.price) : '',
        is_available: menu.is_available !== false
      });
      setImagePreview(imageUrl);
      setSelectedDishIds(menu.dishes ? menu.dishes.map(dish => dish.id) : []);
    } else {
      setFormData({
        name: '',
        description: '',
        image_url: '',
        price: '',
        is_available: true
      });
      setImagePreview('');
      setSelectedDishIds([]);
    }
    setErrors({});
  }, [menu, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (selectedDishIds.length === 0) newErrors.dishes = 'Au moins un plat doit être sélectionné';
    const priceNum = parseFloat(formData.price);
    if (!formData.price || isNaN(priceNum) || priceNum <= 0) {
      newErrors.price = 'Le prix du menu est requis et doit être supérieur à 0';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toastError('Veuillez renseigner le nom et sélectionner au moins un plat.');
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        dish_ids: selectedDishIds
      };

      await onSuccess(submitData);
      toastSuccess(menu ? 'Menu mis à jour avec succès.' : 'Menu créé avec succès.');
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setErrors({ general: 'Erreur lors de la sauvegarde' });
      toastError(error.response?.data?.detail || 'Erreur lors de la sauvegarde du menu.');
    } finally {
      setLoading(false);
    }
  };

  const selectRandomImage = () => {
    const randomImage = defaultImages[Math.floor(Math.random() * defaultImages.length)];
    setFormData(prev => ({ ...prev, image_url: randomImage }));
    setImagePreview(randomImage);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validation de base
    if (!file.type.startsWith('image/')) {
      setErrors({ ...errors, image: 'Le fichier doit être une image' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      setErrors({ ...errors, image: 'L\'image ne doit pas dépasser 5MB' });
      return;
    }

    try {
      setUploadingImage(true);
      setErrors({ ...errors, image: null });

      // Créer FormData pour l'upload
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('folder', 'menus');

      // Upload vers Digital Ocean Spaces
      const response = await fetch('http://localhost:8000/management/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('restaurantToken')}`
        },
        body: uploadData
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }

      const result = await response.json();

      // Mettre à jour le form avec l'URL uploadée
      setFormData(prev => ({ ...prev, image_url: result.image_url }));
      setImagePreview(result.image_url);

    } catch (error) {
      console.error('Erreur upload:', error);
      setErrors({ ...errors, image: 'Erreur lors de l\'upload de l\'image' });
    } finally {
      setUploadingImage(false);
    }
  };

  const toggleDishSelection = (dishId) => {
    setSelectedDishIds(prev =>
      prev.includes(dishId)
        ? prev.filter(id => id !== dishId)
        : [...prev, dishId]
    );
  };

  const selectedDishes = availableDishes?.filter(dish => selectedDishIds.includes(dish.id)) || [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
      <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '760px', width: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>
            {menu ? 'Modifier le menu' : 'Créer un menu'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={{ flexShrink: 0, border: 'none', cursor: 'pointer', width: '38px', height: '38px', borderRadius: '999px', background: 'transparent', color: 'rgba(5,16,4,0.4)', display: 'grid', placeItems: 'center' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(5,16,4,0.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '26px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {errors.general && (
            <div style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '16px', padding: '12px 16px', fontSize: '13.5px' }}>
              {errors.general}
            </div>
          )}

          {/* Informations de base */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '18px' }}>
            <div>
              <label style={labelStyle}>Nom du menu *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                style={inputStyle}
                placeholder="Ex: Menu du Jour"
              />
              {errors.name && <p style={errorStyle}>{errors.name}</p>}
            </div>

            <div>
              <label style={labelStyle}>Prix du menu (€) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                style={inputStyle}
                placeholder="Ex: 12.90"
              />
              {errors.price && <p style={errorStyle}>{errors.price}</p>}
            </div>
          </div>

          {/* Image */}
          <div>
            <label style={labelStyle}>Image du menu</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="flex gap-2.5 flex-wrap">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  style={{ flex: '1 1 200px', fontSize: '13px', color: 'rgba(5,16,4,0.7)' }}
                />
                <button
                  type="button"
                  onClick={selectRandomImage}
                  disabled={uploadingImage}
                  className="inline-flex items-center gap-2"
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13.5px', minHeight: '42px', padding: '0 18px', borderRadius: '999px', opacity: uploadingImage ? 0.5 : 1 }}
                >
                  <ImageIcon size={15} />
                  Image par défaut
                </button>
              </div>

              {uploadingImage && (
                <div className="flex items-center gap-2" style={{ fontSize: '13px', color: PRIMARY }}>
                  <span style={{ width: '16px', height: '16px', borderRadius: '999px', border: '2px solid rgba(5,70,37,0.25)', borderTopColor: PRIMARY }} className="animate-spin" />
                  Upload en cours...
                </div>
              )}

              {errors.image && <p style={errorStyle}>{errors.image}</p>}

              {(formData.image_url || imagePreview) && (
                <div style={{ borderRadius: '18px', overflow: 'hidden' }}>
                  <img
                    src={imagePreview || formData.image_url}
                    alt="Aperçu du menu"
                    className="w-full h-32 object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+non+disponible';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              style={textareaStyle}
              placeholder="Décrivez votre menu..."
            />
          </div>

          {/* Sélection des plats */}
          <div>
            <label style={labelStyle}>Plats du menu * ({selectedDishIds.length} sélectionnés)</label>
            {errors.dishes && <p style={{ ...errorStyle, marginBottom: '8px' }}>{errors.dishes}</p>}

            <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '10px', maxHeight: '260px', overflowY: 'auto' }}>
              {availableDishes && availableDishes.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {availableDishes.map((dish) => {
                    const selected = selectedDishIds.includes(dish.id);
                    return (
                      <div
                        key={dish.id}
                        onClick={() => toggleDishSelection(dish.id)}
                        className="flex items-center gap-3"
                        style={{
                          cursor: 'pointer', borderRadius: '16px', padding: '12px 14px',
                          border: selected ? `2px solid ${PRIMARY}` : '1px solid rgba(5,16,4,0.1)',
                          background: selected ? '#FCFBF5' : 'transparent',
                        }}
                      >
                        <span style={{ flex: 'none', width: '22px', height: '22px', borderRadius: '7px', display: 'grid', placeItems: 'center', fontSize: '12px', fontWeight: 700, background: selected ? PRIMARY : 'transparent', color: selected ? '#FBF9D9' : 'transparent', border: selected ? 'none' : '2px solid rgba(5,16,4,0.22)' }}>✓</span>
                        <div className="flex-1" style={{ minWidth: 0 }}>
                          <h4 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '15px' }} className="truncate">{dish.name}</h4>
                          <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'rgba(5,16,4,0.6)' }} className="truncate">{dish.description}</p>
                          <div className="flex items-center gap-3" style={{ marginTop: '4px', fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.55)' }}>
                            <span>{dish.price ? `${dish.price}€` : 'Prix non défini'}</span>
                            <span>{dish.category_name || 'Non catégorisé'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px 16px', color: 'rgba(5,16,4,0.5)' }}>
                  <p style={{ margin: 0, fontSize: '14px' }}>Aucun plat disponible</p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Créez d'abord des plats avant de créer un menu</p>
                </div>
              )}
            </div>
          </div>

          {/* Disponibilité */}
          <div className="flex items-center gap-3">
            <Toggle checked={formData.is_available} onChange={(v) => setFormData(prev => ({ ...prev, is_available: v }))} />
            <span style={{ fontSize: '14px', fontWeight: 600 }}>Menu disponible</span>
          </div>

          {/* Résumé */}
          {selectedDishes.length > 0 && (
            <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '18px' }}>
              <h4 style={{ margin: '0 0 10px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '16px' }}>Résumé du menu</h4>
              <div className="flex flex-wrap gap-2">
                {selectedDishes.map((dish) => (
                  <span
                    key={dish.id}
                    style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', background: '#F1F8E8', color: '#37702C', padding: '6px 14px', borderRadius: '999px' }}
                  >
                    {dish.name} — {dish.price}€
                  </span>
                ))}
              </div>
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(5,16,4,0.1)' }}>
                <p style={{ margin: 0, fontSize: '13.5px', color: 'rgba(5,16,4,0.7)' }}>
                  <strong style={{ color: '#051004' }}>Prix total estimé :</strong> {selectedDishes.reduce((sum, dish) => sum + (dish.price || 0), 0).toFixed(2)}€
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2.5 flex-wrap" style={{ marginTop: '8px', paddingTop: '18px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '46px', padding: '0 22px', borderRadius: '999px' }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || selectedDishIds.length === 0}
              style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '46px', padding: '0 24px', borderRadius: '999px', opacity: (loading || selectedDishIds.length === 0) ? 0.6 : 1 }}
            >
              {loading ? 'Enregistrement...' : (menu ? 'Modifier' : 'Créer le menu')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MenuModal;
